import { createContext, useContext, useEffect, useState } from "react";

// Simple i18n dictionary. Arabic default.
const dict = {
  ar: {
    brand: "MERQAV",
    brand_en: "MERQAV",
    slogan: "نربط الصناعة... ونبني الثقة.",
    slogan_alt: "منصة تنسيق المشاريع الصناعية والتوريد",
    description: "MERQAV منصة تنسيق المشاريع الصناعية والتوريد التي تربط العملاء بالورش والمصانع والمصنعين والمفتشين ومزودي الخدمات الصناعية في المملكة العربية السعودية.",
    nav: {
      home: "الرئيسية",
      how: "كيف تعمل MERQAV",
      capabilities: "القدرات التصنيعية",
      industries: "القطاعات",
      request_part: "اطلب تصنيع قطعة",
      partner: "انضم كشريك تصنيع",
      about: "من نحن",
      faq: "الأسئلة الشائعة",
      contact: "تواصل معنا",
      login: "تسجيل الدخول",
      register: "إنشاء حساب",
      dashboard: "لوحة التحكم",
      logout: "تسجيل الخروج",
    },
    cta: {
      request_part: "اطلب تصنيع قطعة",
      request_inspection: "اطلب زيارة فنية",
      partner: "انضم كشريك تصنيع",
    },
    common: {
      loading: "جارٍ التحميل...",
      submit: "إرسال",
      cancel: "إلغاء",
      save: "حفظ",
      next: "التالي",
      previous: "السابق",
      approve: "اعتماد",
      reject: "رفض",
      view: "عرض",
      edit: "تعديل",
      delete: "حذف",
      search: "بحث",
      currency: "ر.س",
      new: "جديد",
      status: "الحالة",
    },
  },
  en: {
    brand: "MERQAV",
    brand_en: "MERQAV",
    slogan: "Connecting Industry. Building Trust.",
    slogan_alt: "Industrial Project & Supply Coordination Platform",
    description: "MERQAV is an Industrial Project & Supply Coordination Platform connecting customers with qualified workshops, factories, manufacturers, inspectors, and industrial service providers across Saudi Arabia.",
    nav: {
      home: "Home",
      how: "How MERQAV Works",
      capabilities: "Capabilities",
      industries: "Industries",
      request_part: "Request a Part",
      partner: "Become a Partner",
      about: "About",
      faq: "FAQ",
      contact: "Contact",
      login: "Login",
      register: "Register",
      dashboard: "Dashboard",
      logout: "Logout",
    },
    cta: {
      request_part: "Request a Part",
      request_inspection: "Request an Inspection",
      partner: "Become a Manufacturing Partner",
    },
    common: {
      loading: "Loading...",
      submit: "Submit",
      cancel: "Cancel",
      save: "Save",
      next: "Next",
      previous: "Previous",
      approve: "Approve",
      reject: "Reject",
      view: "View",
      edit: "Edit",
      delete: "Delete",
      search: "Search",
      currency: "SAR",
      new: "New",
      status: "Status",
    },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("merqav_lang") || localStorage.getItem("metrax_lang") || "ar");

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("merqav_lang", lang);
  }, [lang]);

  const t = (path) => {
    const keys = path.split(".");
    let cur = dict[lang];
    for (const k of keys) cur = cur?.[k];
    return cur ?? path;
  };

  const toggle = () => setLang((l) => (l === "ar" ? "en" : "ar"));

  return (
    <I18nContext.Provider value={{ lang, setLang, t, toggle }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);

// Status label mapping
export const STATUS_LABELS = {
  received: { ar: "تم استلام الطلب", en: "Received", color: "badge-neutral" },
  under_review: { ar: "الطلب تحت المراجعة", en: "Under Review", color: "badge-review" },
  inspection_needed: { ar: "يلزم فحص فني", en: "Inspection Needed", color: "badge-inspection" },
  inspection_scheduled: { ar: "تم تحديد موعد الفحص", en: "Inspection Scheduled", color: "badge-inspection" },
  inspection_completed: { ar: "اكتمل الفحص الفني", en: "Inspection Completed", color: "badge-inspection" },
  preparing_quote: { ar: "جاري إعداد عرض السعر", en: "Preparing Quote", color: "badge-pending" },
  quote_ready: { ar: "عرض السعر جاهز", en: "Quote Ready", color: "badge-pending" },
  awaiting_approval: { ar: "بانتظار موافقتك", en: "Awaiting Your Approval", color: "badge-pending" },
  approved: { ar: "تم اعتماد الطلب", en: "Approved", color: "badge-ready" },
  manufacturing_prep: { ar: "جاري التجهيز للتصنيع", en: "Manufacturing Prep", color: "badge-manufacturing" },
  manufacturing_started: { ar: "بدأ التصنيع", en: "Manufacturing Started", color: "badge-manufacturing" },
  manufacturing_in_progress: { ar: "التصنيع قيد التنفيذ", en: "In Progress", color: "badge-manufacturing" },
  quality_inspection: { ar: "فحص الجودة", en: "Quality Inspection", color: "badge-inspection" },
  ready_for_delivery: { ar: "جاهز للتسليم", en: "Ready for Delivery", color: "badge-ready" },
  out_for_delivery: { ar: "خرج للتسليم", en: "Out for Delivery", color: "badge-ready" },
  delivered: { ar: "تم التسليم", en: "Delivered", color: "badge-ready" },
  completed: { ar: "مكتمل", en: "Completed", color: "badge-ready" },
  cancelled: { ar: "ملغي", en: "Cancelled", color: "badge-rejected" },
  needs_review: { ar: "توجد ملاحظة", en: "Needs Review", color: "badge-pending" },
  pending: { ar: "قيد المراجعة", en: "Pending", color: "badge-pending" },
  approved_ws: { ar: "معتمد", en: "Approved", color: "badge-ready" },
  rejected: { ar: "مرفوض", en: "Rejected", color: "badge-rejected" },
  invited: { ar: "بانتظار عرض السعر", en: "Awaiting Quote", color: "badge-pending" },
  quoted: { ar: "تم تقديم العرض", en: "Quoted", color: "badge-review" },
  sent: { ar: "تم الإرسال", en: "Sent", color: "badge-review" },
  submitted: { ar: "مقدم", en: "Submitted", color: "badge-review" },
};

export function statusLabel(key, lang = "ar") {
  return STATUS_LABELS[key]?.[lang] || key;
}
export function statusColor(key) {
  return STATUS_LABELS[key]?.color || "badge-neutral";
}
